import React from 'react';
import { computeTotalPages, DEFAULT_PAGE_SIZE, parsePageSize } from '../../../utils/paginationUtils';
import './ListScreenPaginationBar.css';

const DEFAULT_ENTRY_OPTIONS = [
  { value: '10', label: '10' },
  { value: '30', label: '30' },
  { value: '50', label: '50' },
  { value: 'all', label: 'All' }
];

/**
 * Bottom bar: Show [n] entries, optional left slot (e.g. Event Logs), Page [x] of [y]
 * with first / previous / next / last navigation.
 * Pass totalRecords when available — total pages are computed from totalRecords / page size
 * so page count stays correct when entries-per-page changes.
 */
const ListScreenPaginationBar = ({
  entriesValue,
  onEntriesChange,
  entryOptions = DEFAULT_ENTRY_OPTIONS,
  leftSlot = null,
  showPageSelector = true,
  currentPage = 1,
  totalPages: totalPagesProp = 1,
  totalRecords = null,
  onPageChange
}) => {
  const rawEv = entriesValue ?? String(DEFAULT_PAGE_SIZE);
  const selectValue =
    rawEv === 9999 || rawEv === '9999' || rawEv === 'all' || rawEv === 'All' ? 'all' : String(rawEv);

  const resolvedTotalPages =
    totalRecords != null && totalRecords !== undefined
      ? computeTotalPages(totalRecords, selectValue === 'all' ? 'all' : parsePageSize(selectValue))
      : Math.max(1, Number(totalPagesProp) || 1);

  const safeTotal = Math.max(1, resolvedTotalPages);
  const rawPage = Number(currentPage) || 1;
  const safeCurrent = Math.min(Math.max(1, rawPage), safeTotal);

  const handleEntriesChange = (value) => {
    onEntriesChange(value);
  };

  const navDisabled = !onPageChange;
  const isAllEntries = selectValue === 'all';
  const pageNavigationDisabled = navDisabled || isAllEntries;
  const isFirstPage = safeCurrent <= 1;
  const isLastPage = safeCurrent >= safeTotal;

  const goToPage = (page) => {
    if (pageNavigationDisabled) return;
    onPageChange(Math.min(Math.max(1, page), safeTotal));
  };

  return (
    <div className={`list-screen-pagination-section ${leftSlot ? 'has-left-slot' : ''} attached`}>
      <div className="list-screen-pagination-left">
        <div className="list-screen-entries-selector">
          <span>Show</span>
          <select
            className="list-screen-entries-select"
            value={selectValue}
            onChange={(e) => handleEntriesChange(e.target.value)}
          >
            {entryOptions.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label ?? opt.value}
              </option>
            ))}
          </select>
          <span>entries</span>
        </div>
        {leftSlot}
      </div>
      {showPageSelector && (
        <div className="list-screen-pagination-right">
          <div className="list-screen-page-selector">
            <div className="list-screen-page-nav">
              <button
                type="button"
                className="list-screen-page-nav-btn"
                onClick={() => goToPage(1)}
                disabled={pageNavigationDisabled || isFirstPage}
                aria-label="First page"
                title="First page"
              >
                &laquo;&laquo;
              </button>
              <button
                type="button"
                className="list-screen-page-nav-btn"
                onClick={() => goToPage(safeCurrent - 1)}
                disabled={pageNavigationDisabled || isFirstPage}
                aria-label="Previous page"
                title="Previous page"
              >
                &lsaquo;
              </button>
            </div>
            <span>Page</span>
            <select
              className="list-screen-page-number-select"
              value={safeCurrent}
              onChange={(e) => goToPage(Number(e.target.value))}
              disabled={pageNavigationDisabled}
            >
              {Array.from({ length: safeTotal }, (_, i) => i + 1).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <span>of {safeTotal}</span>
            <div className="list-screen-page-nav">
              <button
                type="button"
                className="list-screen-page-nav-btn"
                onClick={() => goToPage(safeCurrent + 1)}
                disabled={pageNavigationDisabled || isLastPage}
                aria-label="Next page"
                title="Next page"
              >
                &rsaquo;
              </button>
              <button
                type="button"
                className="list-screen-page-nav-btn"
                onClick={() => goToPage(safeTotal)}
                disabled={pageNavigationDisabled || isLastPage}
                aria-label="Last page"
                title="Last page"
              >
                &raquo;&raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListScreenPaginationBar;
export { DEFAULT_ENTRY_OPTIONS };
